import React, { useState, useRef, useEffect } from 'react';
import { Printer, X, Edit, Save } from 'lucide-react';
import axios from 'axios';

import logoImage from './logo.png';

const RentalContractPrint = ({ reservation = {}, client = {}, vehicle = {}, onClose = () => { } }) => {
    const [logo1Error, setLogo1Error] = useState(false);
    const [logoError, setLogoError] = useState(false);
    const [logo2Error, setLogo2Error] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [logoUrl, setLogoUrl] = useState(null);
    const [conditions, setConditions] = useState([]);
    const [showLocataireSign, setShowLocataireSign] = useState(true);
    const [showDirectionSign, setShowDirectionSign] = useState(true);
    const printContentRef = useRef(null);

    const [formData, setFormData] = useState({
        // Contract information
        contract_number: '',
        contract_date: new Date().toLocaleDateString('fr-FR'),

        // Client information
        nom: '',
        prenom: '',
        date_naissance: '',
        lieu_naissance: '',
        adresse: '',
        telephone: '',
        numero_piece: '',
        date_delivrance_piece: '',
        permis_delivre_le: '',
        numero_permis: '',
        permis_validite: '',
        passport_delivre_le: '',
        numero_passport: '',
        reserve_conducteur: '',
        lieu_permet: '',
        lieu_passport: '',

        // Second driver information
        conducteur2_nom: '',
        conducteur2_prenom: '',
        conducteur2_date_naissance: '',
        conducteur2_lieu_naissance: '',
        conducteur2_adresse: '',
        conducteur2_telephone: '',
        conducteur2_numero_piece: '',
        conducteur2_date_delivrance_piece: '',
        conducteur2_permis_delivre_le: '',
        conducteur2_numero_permis: '',
        conducteur2_permis_validite: '',
        conducteur2_lieu_permet: '',
        conducteur2_lieu_passport: '',

        // Vehicle information
        marque: '',
        type: '',
        couleur: '',
        immatriculation: '',
        numero_serie: '',
        annee_circulation: '',
        carrosserie: '',
        energie: '',
        puissance: '',
        places: '',
        poids: '',
        charge_utile: '',
        precedent_immatriculation: '',

        // Rental information
        date_depart: '',
        heure_depart: '',
        date_retour: '',
        heure_retour: '',
        km_depart: '',
        km_retour: '',
        prix_jour: '',
        nombre_jours: '',
        equipement_depart: 'Clés, Documents, Roue de secours, Crick',

        // Additional missing fields
        type_contrat: '',
        caution: '',
        mode_paiement: '',
        observations: '',
    });

    // Simulate fetching data from backend
    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const token = localStorage.getItem('token');
                if (token) {
                    try {
                        const infoRes = await axios.get('https://krili-loto.com/api/account-info', {
                            headers: { Authorization: `Bearer ${token}` }
                        });
                        if (infoRes.data && infoRes.data.logo) {
                            setLogoUrl(`https://krili-loto.com/api/uploads/${infoRes.data.logo}`);
                        }
                        // Set conditions
                        try {
                            const parsedConditions = infoRes.data.conditions ?
                                (typeof infoRes.data.conditions === 'string' ? JSON.parse(infoRes.data.conditions) : infoRes.data.conditions)
                                : [];
                            setConditions(Array.isArray(parsedConditions) ? parsedConditions : []);
                        } catch (e) {
                            console.error('Error parsing conditions in print:', e);
                            setConditions([]);
                        }
                    } catch (e) {
                        console.error('Error fetching account info for logo:', e);
                    }
                }

                const calculateNombreJours = () => {
                    if (!reservation.date_depart || !reservation.date_retour_prevue) return '';
                    try {
                        const start = new Date(reservation.date_depart);
                        const end = new Date(reservation.date_retour_prevue);
                        const diffTime = Math.abs(end - start);
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                        return diffDays.toString();
                    } catch (error) {
                        console.error('Error calculating rental days:', error);
                        return '';
                    }
                };

                // Simulate API call
                setTimeout(() => {
                    setFormData(prev => ({
                        ...prev,
                        // Contract info
                        contract_number: reservation.id || '',
                        type_contrat: reservation.type_contrat || 'TYPE 1',
                        caution: reservation.caution || '',
                        mode_paiement: reservation.mode_paiement || '',
                        observations: reservation.observations || '',

                        // Client info
                        nom: client.nom || '',
                        prenom: client.prenom || '',
                        date_naissance: client.date_naissance || '',
                        lieu_naissance: client.lieu_naissance || '',
                        adresse: client.adresse || '',
                        telephone: client.telephone || '',
                        numero_piece: client.numero_permis || client.numero_passport || '',
                        date_delivrance_piece: client.date_delivrance_permis || '',
                        permis_delivre_le: client.permis_delivre_le || '',
                        numero_permis: client.numero_permis || '',
                        permis_validite: formatDateFromDateTime(client.permis_validite || ''),
                        passport_delivre_le: client.passport_delivre_le || '',
                        numero_passport: client.numero_passport || '',
                        lieu_permet: client.lieu_permet || '',
                        lieu_passport: client.lieu_passport || '',

                        // Second driver info
                        conducteur2_nom: client.conducteur2_nom || '',
                        conducteur2_prenom: client.conducteur2_prenom || '',
                        conducteur2_date_naissance: client.conducteur2_date_naissance || '',
                        conducteur2_lieu_naissance: client.conducteur2_lieu_naissance || '',
                        conducteur2_adresse: client.conducteur2_adresse || '',
                        conducteur2_telephone: client.conducteur2_telephone || '',
                        conducteur2_numero_piece: client.conducteur2_numero_permis || client.conducteur2_numero_passport || '',
                        conducteur2_date_delivrance_piece: client.conducteur2_date_delivrance_permis || '',
                        conducteur2_permis_delivre_le: client.conducteur2_permis_delivre_le || '',
                        conducteur2_numero_permis: client.conducteur2_numero_permis || '',
                        conducteur2_permis_validite: formatDateFromDateTime(client.conducteur2_permis_validite || ''),
                        conducteur2_lieu_permet: client.conducteur2_permis_delivre_le || '',
                        conducteur2_lieu_passport: client.conducteur2_lieu_passport || '',

                        // Vehicle info
                        marque: vehicle.marque || '',
                        type: vehicle.modele || '',
                        couleur: vehicle.couleur || '',
                        immatriculation: vehicle.matricule || '',
                        numero_serie: vehicle.numero_chassis || '',
                        annee_circulation: vehicle.annee || '',
                        carrosserie: vehicle.carrosserie || '',
                        energie: vehicle.carburant || '',
                        puissance: vehicle.puissance || '',
                        places: vehicle.nb_places || '',
                        poids: vehicle.poids || '',
                        charge_utile: vehicle.charge_utile || '',
                        precedent_immatriculation: vehicle.precedent_immatriculation || '',

                        // Rental info
                        date_depart: reservation.date_depart,
                        heure_depart: formatTimeFromDateTime(reservation.date_depart),
                        date_retour: reservation.date_retour_prevue,
                        heure_retour: formatTimeFromDateTime(reservation.date_retour_prevue),
                        km_depart: reservation.km_depart || '',
                        km_retour: reservation.km_retour || '',
                        prix_jour: reservation.prix_base || '',
                        nombre_jours: calculateNombreJours(),
                    }));
                    setIsLoading(false);
                }, 500);
            } catch (error) {
                console.error('Error fetching data:', error);
                setIsLoading(false);
            }
        };

        fetchData();
    }, [reservation, client, vehicle]);

    const formatTimeFromDateTime = (dateTimeString) => {
        if (!dateTimeString) return '00:00';
        try {
            const date = new Date(dateTimeString);
            if (isNaN(date.getTime())) return '00:00';
            return date.toLocaleTimeString('fr-FR', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });
        } catch (error) {
            console.error('Error formatting time:', error);
            return '00:00';
        }
    };

    const formatDateFromDateTime = (dateTimeString) => {
        if (!dateTimeString) return '';
        try {
            const date = new Date(dateTimeString);
            if (isNaN(date.getTime())) return '';
            return date.toLocaleDateString('fr-FR');
        } catch (error) {
            console.error('Error formatting date:', error);
            return '';
        }
    };

    const formatDateForDisplay = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '';
            return date.toLocaleDateString('fr-FR');
        } catch (error) {
            console.error('Error formatting date:', error);
            return '';
        }
    };

    const handlePrint = () => {
        const printContent = printContentRef.current;
        const printWindow = window.open('', '_blank', 'width=800,height=600');
        if (!printWindow) return;
        const content = printContent.innerHTML;

        printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
            <title>Contrat de Location #${reservation.id || 'N/A'}</title>
            <meta charset="UTF-8">
            <style>
                /* All your current styles exactly as they appear in your inline styles */
                @page {
                    margin: 0;
                    size: A4 portrait;
                    padding: 0;
                }
                body, html {
                    margin: 0 !important;
                    padding: 0 !important;
                    width: 100%;
                    height: 100%;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                    font-family: Arial, sans-serif;
                }
                .contract-container { width: 100%; margin: 0; padding: 0; }
                .contract-page { padding: 8px 20px; }
                table { width: 100%; border-collapse: collapse; font-size: 12px; }
                td { border: 1px solid black; vertical-align: top; }
                .text-center { text-align: center; }
                .font-semibold { font-weight: bold; }
                .border-b { border-bottom: 1px solid black; }
                .border-black { border-color: black; }
                .arabic-text { text-align: right; direction: rtl; font-weight: 900; }
                .editable-input { 
                    width: 100%; 
                    border: none; 
                    outline: none; 
                    background: transparent; 
                    font-size: inherit; 
                    font-weight: bold;
                    padding: 0;
                    margin: 0;
                }
                .conditions-container {
                    margin-top: 20px;
                }
                .condition-item {
                    display: flex;
                    margin-bottom: 4px;
                    padding-bottom: 2px;
                }
                .condition-fr {
                    flex: 1;
                    font-size: 13px;
                    text-align: left;
                }
                .condition-ar {
                    flex: 1;
                    font-size: 15px;
                    text-align: right;
                    direction: rtl;
                    font-weight: bold;
                }
                .page-break {
                    page-break-before: always;
                }
                .signature-section {
                    margin-top: 30px;
                    display: flex;
                    justify-content: space-between;
                }
                .signature-line {
                    width: 45%;
                    border-top: 1px solid black;
                    margin-top: 50px;
                    padding-top: 5px;
                    text-align: center;
                }
                .signature-title {
                    font-weight: bold;
                    margin-bottom: 5px;
                }
                @media print {
                    @page { margin: 0; padding: 0; size: A4 portrait; }
                    body, html { margin: 0; padding: 0; width: 100%; height: 100%; }
                    * { -webkit-transform: scale(1); transform: scale(1); }
                }
            </style>
        </head>
        <body>
            <div class="contract-container">${content}</div>
        </body>
        </html>
    `);

        printWindow.document.close();
        printWindow.onload = function () {
            setTimeout(() => {
                printWindow.focus();
                printWindow.print();
                printWindow.onafterprint = function () {
                    printWindow.close();
                };
                setTimeout(() => {
                    if (!printWindow.closed) {
                        printWindow.close();
                    }
                }, 1000);
            }, 500);
        };
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSave = () => {
        setIsEditing(false);
        console.log('Saving contract data:', formData);
    };

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '';
            return date.toISOString().split('T')[0];
        } catch (error) {
            console.error('Error formatting date for input:', error);
            return '';
        }
    };

    // Fixed EditableField component
    const EditableField = ({ name, value, type = 'text', placeholder = '' }) => {
        if (isEditing) {
            if (type === 'date') {
                return (
                    <input
                        type="date"
                        name={name}
                        value={formatDateForInput(value)}
                        onChange={handleInputChange}
                        className="editable-input"
                        placeholder={placeholder}
                    />
                );
            }
            return (
                <input
                    type={type}
                    name={name}
                    value={value || ''}
                    onChange={handleInputChange}
                    className="editable-input"
                    placeholder={placeholder}
                />
            );
        }

        // For display mode - format dates properly
        if (type === 'date') {
            return <span>{formatDateForDisplay(value) || placeholder}</span>;
        }
        return <span>{value || placeholder}</span>;
    };

    if (isLoading) {
        return (
            <div style={{
                minHeight: '100vh',
                backgroundColor: '#f7fafc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{
                        animation: 'spin 1s linear infinite',
                        borderRadius: '50%',
                        height: '48px',
                        width: '48px',
                        border: '2px solid #3182ce',
                        borderBottomColor: 'transparent',
                        margin: '0 auto'
                    }}></div>
                    <p style={{ marginTop: '16px', color: '#718096' }}>Chargement des données du contrat...</p>
                </div>
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: '#f7fafc',
            padding: '16px'
        }}>
            {/* Header Section */}
            <div style={{
                maxWidth: '72rem',
                margin: '0 auto 24px'
            }}>
                <div style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                    padding: '16px'
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <h2 style={{
                            fontSize: '1.5rem',
                            fontWeight: 'bold',
                            color: '#2d3748'
                        }}>Aperçu du Contrat de Location</h2>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            {isEditing ? (
                                <button
                                    onClick={handleSave}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        backgroundColor: '#38a169',
                                        color: 'white',
                                        padding: '8px 16px',
                                        borderRadius: '8px',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontWeight: '500',
                                        transition: 'background-color 0.15s ease-in-out'
                                    }}
                                    onMouseOver={(e) => e.target.style.backgroundColor = '#2f855a'}
                                    onMouseOut={(e) => e.target.style.backgroundColor = '#38a169'}
                                >
                                    <Save size={20} style={{ marginRight: '8px' }} />
                                    Enregistrer
                                </button>
                            ) : (
                                <button
                                    onClick={() => setIsEditing(true)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        backgroundColor: '#3182ce',
                                        color: 'white',
                                        padding: '8px 16px',
                                        borderRadius: '8px',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontWeight: '500',
                                        transition: 'background-color 0.15s ease-in-out'
                                    }}
                                    onMouseOver={(e) => e.target.style.backgroundColor = '#2b6cb0'}
                                    onMouseOut={(e) => e.target.style.backgroundColor = '#3182ce'}
                                >
                                    <Edit size={20} style={{ marginRight: '8px' }} />
                                    Modifier
                                </button>
                            )}
                            <div style={{ display: 'flex', gap: '15px', alignItems: 'center', backgroundColor: '#edf2f7', padding: '8px 15px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#4a5568' }}>
                                    <input type="checkbox" checked={showLocataireSign} onChange={(e) => setShowLocataireSign(e.target.checked)} />
                                    Locataire
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#4a5568' }}>
                                    <input type="checkbox" checked={showDirectionSign} onChange={(e) => setShowDirectionSign(e.target.checked)} />
                                    Direction
                                </label>
                            </div>
                            <button
                                onClick={handlePrint}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    backgroundColor: '#2d3748',
                                    color: 'white',
                                    padding: '8px 16px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontWeight: '500',
                                    transition: 'background-color 0.15s ease-in-out'
                                }}
                                onMouseOver={(e) => e.target.style.backgroundColor = '#1a202c'}
                                onMouseOut={(e) => e.target.style.backgroundColor = '#2d3748'}
                            >
                                <Printer size={20} style={{ marginRight: '8px' }} />
                                Imprimer
                            </button>
                            <button
                                onClick={onClose}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    backgroundColor: '#e53e3e',
                                    color: 'white',
                                    padding: '8px 16px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontWeight: '500',
                                    transition: 'background-color 0.15s ease-in-out'
                                }}
                                onMouseOver={(e) => e.target.style.backgroundColor = '#c53030'}
                                onMouseOut={(e) => e.target.style.backgroundColor = '#e53e3e'}
                            >
                                <X size={20} style={{ marginRight: '8px' }} />
                                Fermer
                            </button>
                        </div>
                    </div>
                    <p style={{ color: '#718096', marginTop: '8px' }}>Vérifiez toutes les informations avant d'imprimer le contrat.</p>
                </div>
            </div>

            {/* Contract Preview */}
            <div style={{
                maxWidth: '72rem',
                margin: '0 auto'
            }}>
                <div style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
                    border: '2px solid #e2e8f0',
                    overflow: 'hidden'
                }}>
                    <div style={{
                        backgroundColor: '#f9fafb',
                        padding: '8px 16px',
                        borderBottom: '1px solid #e2e8f0'
                    }}>
                        <p style={{
                            fontSize: '0.875rem',
                            color: '#718096',
                            fontWeight: '500'
                        }}>Aperçu d'impression - Ce contenu sera imprimé</p>
                    </div>

                    {/* Your existing contract content - unchanged */}
                    <div ref={printContentRef} className="contract-container" style={{
                        backgroundColor: 'white',
                        maxHeight: '100vh',
                        overflowY: 'auto'
                    }}>
                        <div className="contract-page" style={{ marginBottom: '4px' }}>

                            <div className="logo-container" style={{ marginBottom: '16px', textAlign: 'center' }}>
                                {!logoError ? (
                                    <img
                                        src={logoUrl || logoImage}
                                        alt="AUTO DZ LOC"
                                        style={{ width: '1280px', height: '319px', maxWidth: '100%', objectFit: 'contain' }}
                                        onError={() => setLogoError(true)}
                                    />
                                ) : (
                                    <div style={{
                                        backgroundColor: 'black',
                                        color: 'white',
                                        padding: '4px',
                                        borderRadius: '4px',
                                        textAlign: 'center',
                                        margin: '0 auto',
                                        width: 'fit-content'
                                    }}></div>
                                )}
                            </div>
                            <div>
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    marginLeft: '32px',
                                    fontSize: '0.875rem'
                                }}>
                                    <div style={{ textAlign: 'left' }}>
                                        <div style={{ fontWeight: 'bold' }}>N° Contrat :
                                            <EditableField name="contract_number" value={formData.contract_number}
                                                placeholder="/" />
                                            <span className="arabic-text"
                                                style={{ fontWeight: '900', fontSize: '14px', color: '#000000' }}> رقم العقد:</span>
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'left' }}>
                                        <div style={{ fontWeight: 'bold' }}>Date Du Contrat :
                                            <EditableField name="contract_date" value={formData.contract_date}
                                                type="date" />
                                            <span className="arabic-text"
                                                style={{ fontWeight: '900', fontSize: '14px', color: '#000000' }}> تاريخ العقد</span>
                                        </div>
                                    </div>
                                </div>

                                <div style={{
                                    textAlign: 'center',
                                    fontWeight: 'bold',
                                }}>
                                    <div style={{ fontSize: '24px', marginBottom: '2px' }}>عقد كراء</div>
                                    <div style={{ fontSize: '14px', fontWeight: 'normal' }}>(هذا العقد بموجبه يحدد شروط كراء السيارات)</div>
                                </div>
                            </div>

                            <div style={{ marginRight: '30px', marginLeft: '30px' }}>
                                {/* Main Driver Section */}
                                <div style={{
                                    display: 'flex',
                                    fontSize: '13px',
                                    textDecoration: 'underline',
                                    fontWeight: 'bold',
                                    marginBottom: '2px'
                                }}>
                                    <span>Réserver au conducteur :</span>
                                    <span style={{ marginLeft: '375px' }}>خاص ب سائق</span>
                                </div>

                                <div style={{
                                    display: 'flex',
                                    fontSize: '13px',
                                    textDecoration: 'underline',
                                    fontWeight: 'bold'
                                }}>
                                    <div>Lieu de naissance</div>
                                    <div style={{ marginLeft: '150px' }}>date de naissance</div>
                                    <div style={{ marginLeft: '150px' }}>Nom et Prénom</div>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                    <div style={{
                                        flex: 1,
                                        border: '1px solid #000',
                                        minHeight: '25px',
                                        fontSize: '13px',
                                        padding: '1px 0',
                                        paddingLeft: '8px',
                                        fontWeight: 'bold'
                                    }}>
                                        <EditableField name="lieu_naissance" value={formData.lieu_naissance}
                                            placeholder="/" />
                                    </div>
                                    <div style={{
                                        flex: 1,
                                        border: '1px solid #000',
                                        minHeight: '25px',
                                        fontSize: '13px',
                                        padding: '1px 0',
                                        paddingLeft: '8px',
                                        fontWeight: 'bold'
                                    }}>
                                        <EditableField name="date_naissance" value={formData.date_naissance}
                                            type="date" />
                                    </div>
                                    <div style={{
                                        flex: 1,
                                        border: '1px solid #000',
                                        minHeight: '25px',
                                        fontSize: '13px',
                                        padding: '1px 0',
                                        paddingLeft: '8px',
                                        fontWeight: 'bold'
                                    }}>
                                        <div style={{ display: 'flex', gap: '5px' }}>
                                            <EditableField name="nom" value={formData.nom} placeholder="/" />
                                            <EditableField name="prenom" value={formData.prenom} placeholder="/" />
                                        </div>
                                    </div>
                                </div>

                                {/* License Section */}
                                <div style={{
                                    display: 'flex',
                                    whiteSpace: 'nowrap',
                                    fontSize: '13px',
                                    fontWeight: 'bold'
                                }}>
                                    <div>Valide Jusqu'au :</div>
                                    <div style={{ marginLeft: '80px' }}>A</div>
                                    <div style={{ marginLeft: '130px' }}>Delivre le :</div>
                                    <div style={{ marginLeft: '100px' }}>N Permis de conduire&nbsp;&nbsp; <span>رقم</span>
                                    </div>
                                </div>
                                <div style={{
                                    display: 'flex',
                                    whiteSpace: 'nowrap',
                                    fontSize: '13px',
                                    fontWeight: 'bold'
                                }}>
                                    <div style={{ marginLeft: '1px', fontWeight: '900' }}>مدة الصلاحية :</div>
                                    <div style={{ marginLeft: '300px', fontWeight: '900' }}>الصادرة بتاريخ</div>
                                    <div style={{ marginLeft: '170px', fontWeight: '900' }}>رخصة السياقة</div>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <div style={{
                                        flex: 1,
                                        border: '1px solid #000',
                                        minHeight: '25px',
                                        fontSize: '13px',
                                        fontWeight: 'bold',
                                        padding: '1px 0',
                                        paddingLeft: '5px'
                                    }}>
                                        <EditableField name="permis_validite"
                                            placeholder="10ANS" />
                                    </div>
                                    <div style={{
                                        flex: 0.7,
                                        border: '1px solid #000',
                                        minHeight: '25px',
                                        fontSize: '13px',
                                        padding: '1px 0',
                                        fontWeight: 'bold',
                                        paddingLeft: '5px',
                                        textAlign: 'center'
                                    }}>
                                        <EditableField name="lieu_permet" value={formData.lieu_permet} placeholder="/" />
                                    </div>
                                    <div style={{
                                        flex: 1,
                                        border: '1px solid #000',
                                        minHeight: '25px',
                                        fontSize: '13px',
                                        fontWeight: 'bold',
                                        padding: '1px 0',
                                        paddingLeft: '5px'
                                    }}>
                                        <EditableField name="date_delivrance_piece"
                                            value={formData.date_delivrance_piece} type="date" />
                                    </div>
                                    <div style={{
                                        flex: 1,
                                        border: '1px solid #000',
                                        minHeight: '25px',
                                        fontSize: '13px',
                                        padding: '1px 0',
                                        paddingLeft: '5px',
                                        fontWeight: 'bold'
                                    }}>
                                        <EditableField name="numero_permis" value={formData.numero_permis}
                                            placeholder="/" />
                                    </div>

                                </div>

                                {/* Passport Section */}
                                <div style={{
                                    display: 'flex',
                                    whiteSpace: 'nowrap',
                                    fontSize: '13px',
                                    fontWeight: 'bold'
                                }}>
                                    <div>A :</div>
                                    <div style={{ marginLeft: '200px' }}>Delivre le :</div>
                                    <div style={{ marginLeft: '175px' }}>N du passport :&nbsp;&nbsp;<span
                                        style={{ fontWeight: '900' }}>رقم</span></div>
                                </div>
                                <div style={{
                                    display: 'flex',
                                    whiteSpace: 'nowrap',
                                    fontSize: '13px',
                                    fontWeight: 'bold'
                                }}>
                                    <div style={{ marginLeft: '1px', fontWeight: '900' }}>ب :</div>
                                    <div style={{ marginLeft: '200px', fontWeight: '900' }}>الصادرة بتاريخ</div>
                                    <div style={{ marginLeft: '175px', fontWeight: '900' }}>جواز السفر</div>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <div style={{
                                        flex: 1,
                                        border: '1px solid #000',
                                        minHeight: '25px',
                                        fontSize: '13px',
                                        fontWeight: 'bold',
                                        padding: '1px 0',
                                        paddingLeft: '5px'
                                    }}>
                                        <EditableField name="lieu_passport" value={formData.lieu_passport}
                                            placeholder="/" />
                                    </div>
                                    <div style={{
                                        flex: 1,
                                        border: '1px solid #000',
                                        minHeight: '25px',
                                        fontSize: '13px',
                                        padding: '1px 0',
                                        fontWeight: 'bold',
                                        paddingLeft: '5px',
                                        textAlign: 'center'
                                    }}>
                                        <EditableField name="passport_delivre_le" value={formData.passport_delivre_le}
                                            type="date" />
                                    </div>
                                    <div style={{
                                        flex: 1,
                                        border: '1px solid #000',
                                        minHeight: '25px',
                                        fontSize: '13px',
                                        fontWeight: 'bold',
                                        padding: '1px 0',
                                        paddingLeft: '5px'
                                    }}>
                                        <EditableField name="numero_passport" value={formData.numero_passport}
                                            placeholder="/" />
                                    </div>
                                </div>

                                {/* Telephone and Address */}
                                <div style={{ marginTop: '20px' }}>
                                    <table style={{
                                        width: '100%',
                                        borderCollapse: 'collapse',
                                        border: '1px solid #000',
                                        fontSize: '13px'
                                    }}>
                                        <tbody>
                                            <tr>
                                                <td style={{
                                                    border: '1px solid #000',
                                                    padding: '6px',
                                                    fontWeight: 'bold',
                                                    width: '50%'
                                                }}>telephone:
                                                </td>
                                                <td style={{
                                                    border: '1px solid #000',
                                                    padding: '6px',
                                                    fontWeight: 'bold',
                                                    width: '50%'
                                                }}>adresse:
                                                </td>
                                            </tr>
                                            <tr>
                                                <td style={{ border: '1px solid #000', padding: '15px', fontWeight: 'bold' }}>
                                                    <EditableField name="telephone" value={formData.telephone}
                                                        placeholder="/" />
                                                </td>
                                                <td style={{ border: '1px solid #000', padding: '12px', fontWeight: 'bold' }}>
                                                    <EditableField name="adresse" value={formData.adresse}
                                                        placeholder="/" />
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                {/* Second Driver Section */}
                                <div style={{
                                    display: 'flex',
                                    whiteSpace: 'nowrap',
                                    marginTop: '5px',
                                    fontSize: '13px',
                                    fontWeight: 'bold'
                                }}>
                                    <div style={{ marginLeft: '15px' }}>Reserver au deuxieum conductuer :</div>
                                    <div style={{ marginLeft: '310px', fontWeight: '1200' }}>خاص بسائق الثاني</div>
                                </div>

                                <div style={{
                                    display: 'flex',
                                    fontSize: '13px',
                                    textDecoration: 'underline',
                                    fontWeight: 'bold'
                                }}>
                                    <div>Lieu de naissance</div>
                                    <div style={{ marginLeft: '150px' }}>date de naissance</div>
                                    <div style={{ marginLeft: '150px' }}>Nom et Prénom</div>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                    <div style={{
                                        flex: 1,
                                        border: '1px solid #000',
                                        minHeight: '25px',
                                        fontSize: '13px',
                                        padding: '1px 0',
                                        paddingLeft: '5px',
                                        fontWeight: 'bold'
                                    }}>
                                        <EditableField name="conducteur2_lieu_naissance"
                                            value={formData.conducteur2_lieu_naissance}
                                            placeholder="/" />
                                    </div>
                                    <div style={{
                                        flex: 1,
                                        border: '1px solid #000',
                                        minHeight: '25px',
                                        fontSize: '13px',
                                        padding: '1px 0',
                                        paddingLeft: '5px',
                                        fontWeight: 'bold'
                                    }}>
                                        <EditableField name="conducteur2_date_naissance"
                                            value={formData.conducteur2_date_naissance} type="date" />
                                    </div>
                                    <div style={{
                                        flex: 1,
                                        border: '1px solid #000',
                                        minHeight: '25px',
                                        fontSize: '13px',
                                        padding: '1px 0',
                                        paddingLeft: '5px',
                                        fontWeight: 'bold'
                                    }}>
                                        <div style={{ display: 'flex', gap: '5px' }}>
                                            <EditableField name="conducteur2_nom" value={formData.conducteur2_nom}
                                                placeholder="/" />
                                            <EditableField name="conducteur2_prenom" value={formData.conducteur2_prenom}
                                                placeholder="/" />
                                        </div>
                                    </div>
                                </div>

                                <div style={{
                                    display: 'flex',
                                    whiteSpace: 'nowrap',
                                    fontSize: '13px',
                                    fontWeight: 'bold'
                                }}>
                                    <div>Valide Jusqu'au :</div>
                                    <div style={{ marginLeft: '80px' }}>A</div>
                                    <div style={{ marginLeft: '130px' }}>Delivre le :</div>
                                    <div style={{ marginLeft: '100px' }}>N Permis de conduire&nbsp;&nbsp; <span>رقم</span>
                                    </div>
                                </div>
                                <div style={{
                                    display: 'flex',
                                    whiteSpace: 'nowrap',
                                    fontSize: '13px',
                                    fontWeight: 'bold'
                                }}>
                                    <div style={{ marginLeft: '1px', fontWeight: '900' }}>مدة الصلاحية :</div>
                                    <div style={{ marginLeft: '300px', fontWeight: '900' }}>الصادرة بتاريخ</div>
                                    <div style={{ marginLeft: '170px', fontWeight: '900' }}>رخصة السياقة</div>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <div style={{
                                        flex: 1,
                                        border: '1px solid #000',
                                        minHeight: '25px',
                                        fontSize: '13px',
                                        fontWeight: 'bold',
                                        padding: '1px 0',
                                        paddingLeft: '5px'
                                    }}>
                                        <EditableField name="conducteur2_permis_validite"
                                            value={formData.conducteur2_permis_validite}
                                            placeholder="10ANS" />
                                    </div>
                                    <div style={{
                                        flex: 0.7,
                                        border: '1px solid #000',
                                        minHeight: '25px',
                                        fontSize: '13px',
                                        padding: '1px 0',
                                        fontWeight: 'bold',
                                        paddingLeft: '5px',
                                        textAlign: 'center'
                                    }}>
                                        <EditableField name="conducteur2_lieu_permet"
                                            value={formData.conducteur2_lieu_permet}
                                            placeholder="/" />
                                    </div>
                                    <div style={{
                                        flex: 1,
                                        border: '1px solid #000',
                                        minHeight: '25px',
                                        fontSize: '13px',
                                        fontWeight: 'bold',
                                        padding: '1px 0',
                                        paddingLeft: '5px'
                                    }}>
                                        <EditableField name="conducteur2_date_delivrance_piece"
                                            value={formData.conducteur2_date_delivrance_piece} type="date" />
                                    </div>
                                    <div style={{
                                        flex: 1,
                                        border: '1px solid #000',
                                        minHeight: '25px',
                                        fontSize: '13px',
                                        padding: '1px 0',
                                        paddingLeft: '5px',
                                        fontWeight: 'bold'
                                    }}>
                                        <EditableField name="conducteur2_numero_permis"
                                            value={formData.conducteur2_numero_permis}
                                            placeholder="/" />
                                    </div>

                                </div>

                                {/* Vehicle Information */}
                                <div style={{
                                    display: 'flex',
                                    whiteSpace: 'nowrap',
                                    marginTop: '5px',
                                    fontSize: '13px',
                                    fontWeight: 'bold'
                                }}>
                                    <div style={{ marginLeft: '15px' }}>Coordonnee de la voiture:</div>
                                    <div style={{ marginLeft: '310px', fontWeight: '1200' }}>بيانات السيارة</div>
                                </div>

                                <div style={{ marginTop: '15px' }}>
                                    <table style={{
                                        width: '100%',
                                        borderCollapse: 'collapse',
                                        border: '1px solid #000',
                                        fontSize: '12px'
                                    }}>
                                        <tbody>
                                            <tr>
                                                <td style={{
                                                    border: '1px solid #000',
                                                    padding: '8px',
                                                    fontWeight: 'bold',
                                                    width: '30%'
                                                }}>Marque ET COULEUR&nbsp;نوعها
                                                </td>
                                                <td style={{
                                                    border: '1px solid #000',
                                                    padding: '8px',
                                                    fontWeight: 'bold',
                                                    width: '20%'
                                                }}>N° DE CHASSIS
                                                </td>
                                                <td style={{
                                                    border: '1px solid #000',
                                                    padding: '8px',
                                                    fontWeight: 'bold',
                                                    width: '25%'
                                                }}>Matricule :&nbsp;اللوحة
                                                </td>
                                                <td style={{
                                                    border: '1px solid #000',
                                                    padding: '8px',
                                                    fontWeight: 'bold',
                                                    width: '25%'
                                                }}>KM :
                                                </td>
                                            </tr>
                                            <tr>
                                                <td style={{ border: '1px solid #000', padding: '8px', fontWeight: 'bold' }}>
                                                    <div style={{ display: 'flex', gap: '5px' }}>
                                                        <EditableField name="marque" value={formData.marque}
                                                            placeholder="/" />
                                                        <EditableField name="couleur" value={formData.couleur}
                                                            placeholder="" />
                                                    </div>
                                                </td>
                                                <td style={{ border: '1px solid #000', padding: '8px', fontWeight: 'bold' }}>
                                                    <EditableField name="numero_serie" value={formData.numero_serie}
                                                        placeholder="/" />
                                                </td>
                                                <td style={{ border: '1px solid #000', padding: '8px', fontWeight: 'bold' }}>
                                                    <EditableField name="immatriculation" value={formData.immatriculation}
                                                        placeholder="/" />
                                                </td>
                                                <td style={{ border: '1px solid #000', padding: '8px', fontWeight: 'bold' }}>
                                                    <EditableField name="km_depart" value={formData.km_depart}
                                                        placeholder="KM" />
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                {/* Rental Dates and Times */}
                                <div style={{
                                    display: 'flex',
                                    whiteSpace: 'nowrap',
                                    marginTop: '5px',
                                    fontSize: '13px',
                                    fontWeight: 'bold',
                                    justifyContent: 'space-around'
                                }}>
                                    <div>date Sortie: &nbsp; تاريخ الخروج</div>
                                    <div>Heure: &nbsp; الساعة</div>
                                    <div>date d'entrer: &nbsp; تاريخ الدخول</div>
                                    <div>Heure: &nbsp; الساعة</div>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>

                                    <div style={{
                                        flex: 1.25,
                                        border: '1px solid #000',
                                        minHeight: '25px',
                                        fontSize: '12px',
                                        padding: '1px 0',
                                        fontWeight: 'bold',
                                        paddingLeft: '5px',
                                        textAlign: 'center'
                                    }}>
                                        <EditableField name="date_depart" value={formData.date_depart} type="date" />
                                    </div>
                                    <div style={{
                                        flex: 0.75,
                                        border: '1px solid #000',
                                        minHeight: '25px',
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        padding: '1px 0',
                                        paddingLeft: '5px'
                                    }}>
                                        <EditableField name="heure_depart" value={formData.heure_depart}
                                            placeholder="/" />
                                    </div>
                                    <div style={{
                                        flex: 1.25,
                                        border: '1px solid #000',
                                        minHeight: '25px',
                                        fontSize: '12px',
                                        padding: '1px 0',
                                        paddingLeft: '5px',
                                        fontWeight: 'bold'
                                    }}>
                                        <EditableField name="date_retour" value={formData.date_retour} type="date" />
                                    </div>
                                    <div style={{
                                        flex: 0.75,
                                        border: '1px solid #000',
                                        minHeight: '25px',
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        padding: '1px 0',
                                        paddingLeft: '5px'
                                    }}>
                                        <EditableField name="heure_retour" value={formData.heure_retour} />
                                    </div>
                                </div>

                                {/* Signatures Page 1 */}
                                <div className="signature-section" style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between' }}>
                                    {showLocataireSign && (
                                        <div style={{ width: '45%' }}>
                                            <div style={{ fontSize: '13px', fontWeight: 'bold', textAlign: 'center' }}>امضاء و بصمة المستأجر</div>
                                            <div style={{ borderTop: '1px solid black', marginTop: '40px', textAlign: 'center', fontSize: '10px' }}>Signature Locataire</div>
                                        </div>
                                    )}
                                    {showDirectionSign && (
                                        <div style={{ width: '45%' }}>
                                            <div style={{ fontSize: '13px', fontWeight: 'bold', textAlign: 'center' }}>امضاء مدير الوكالة</div>
                                            <div style={{ borderTop: '1px solid black', marginTop: '40px', textAlign: 'center', fontSize: '10px' }}>Signature Direction</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        {/* Removed extra line breaks to save space */}

                        {/* Page 2: Conditions (Only if they exist) */}
                        {conditions && conditions.length > 0 && (
                            <div className="contract-page page-break" style={{ padding: '40px 60px' }}>
                                <div style={{ textAlign: 'center', marginBottom: '30px', borderBottom: '2px solid black', paddingBottom: '10px' }}>
                                    <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: '0' }}>
                                        CONDITIONS DE LOCATION / شروط الكراء
                                    </h2>
                                </div>

                                <div className="conditions-container">
                                    {conditions.map((condition, index) => (
                                        <div key={index} className="condition-item" style={{
                                            display: 'flex',
                                            marginBottom: '4px',
                                            paddingBottom: '2px'
                                        }}>
                                            {condition.fr && (
                                                <div className="condition-fr" style={{
                                                    flex: 1,
                                                    paddingRight: condition.ar ? '15px' : '0',
                                                    fontSize: '13px',
                                                    textAlign: 'left'
                                                }}>
                                                    <strong>{index + 1}.</strong> {condition.fr}
                                                </div>
                                            )}
                                            {condition.ar && (
                                                <div className="condition-ar" style={{
                                                    flex: 1,
                                                    paddingLeft: condition.fr ? '15px' : '0',
                                                    fontSize: '15px',
                                                    textAlign: 'right',
                                                    direction: 'rtl',
                                                    fontWeight: 'bold'
                                                }}>
                                                    {!condition.fr && <strong>{index + 1}.</strong>} {condition.ar}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {/* Signature Section for Page 2 */}
                                <div className="signature-section" style={{ marginTop: '40px', display: 'flex', justifyContent: 'space-between' }}>
                                    {showLocataireSign && (
                                        <div style={{ width: '45%', textAlign: 'center' }}>
                                            <div style={{ fontWeight: 'bold', fontSize: '12px' }}>Le Locataire / المستأجر</div>
                                            <div style={{ borderTop: '1px solid black', marginTop: '40px', fontSize: '10px', color: '#666' }}>
                                                امضاء و بصمة المستأجر (Signature et Empreinte)
                                            </div>
                                        </div>
                                    )}
                                    {showDirectionSign && (
                                        <div style={{ width: '45%', textAlign: 'center' }}>
                                            <div style={{ fontWeight: 'bold', fontSize: '12px' }}>La Direction / الإدارة</div>
                                            <div style={{ borderTop: '1px solid black', marginTop: '40px', fontSize: '10px', color: '#666' }}>
                                                امضاء مدير الوكالة (Signature et Cachet)
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RentalContractPrint;